// Trial Subscription Utilities for Backoffice
// Create and manage trial subscriptions for companies

import { doc, setDoc } from 'firebase/firestore';

/**
 * Create a 1-month PRO trial subscription for a company (Backoffice version)
 * Uses the main app's Firebase database connection
 * @param {Object} appDb - Main app's Firestore database instance
 * @param {string} companyId - Company ID
 * @param {string} adminId - Admin ID who created the trial
 * @returns {Promise<Object>} - Result of subscription creation
 */
export const createTrialSubscriptionForCompany = async (appDb, companyId, adminId) => {
  try {
    console.log(`🎁 Creating 1-month PRO trial for company: ${companyId}`);

    const now = new Date();
    const trialEndDate = new Date(now);
    trialEndDate.setDate(trialEndDate.getDate() + 30); // 30 days from now

    const nextResetDate = new Date(trialEndDate);
    
    // Define PRO plan limits
    const proLimits = {
      firebase_operations: 50000,      // 50K operations per month
      storage_mb: 5000,                 // 5GB storage
      sales_documents: 1000,            // 1000 sales documents
      purchase_documents: 1000,         // 1000 purchase documents
      parties: 500,                     // 500 parties (customers/vendors)
      employees: 50,                    // 50 employees
      items: 2000,                      // 2000 items
      payments: -1,                     // Unlimited payments
      receipts: -1                      // Unlimited receipts
    };

    // Create subscription structure matching the new format
    const subscriptionData = {
      // Base Plan
      basePlan: {
        planId: 'pro',
        planName: 'PRO Plan (Trial)',
        price: 0,
        limits: proLimits,
        startDate: now,
        nextResetDate: nextResetDate,
        nextBillingDate: trialEndDate,
        validityDays: 30,
        bonusDays: {
          total: 0,
          source: null,
          appliedOn: null
        },
        isTrial: true,
        trialEndsAt: trialEndDate
      },

      // Top-ups (empty initially)
      topups: [],

      // Total Entitlements (same as base plan initially)
      totalEntitlements: { ...proLimits },

      // Current Month Usage (all zeros)
      currentMonthUsage: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        purchase_documents: 0,
        parties: 0,
        employees: 0,
        items: 0,
        payments: 0,
        receipts: 0,
        breakdown: {
          basePlan: {
            firebase_operations: 0,
            storage_mb: 0,
            sales_documents: 0,
            purchase_documents: 0,
            parties: 0,
            employees: 0,
            items: 0,
            payments: 0,
            receipts: 0
          },
          topups: []
        }
      },

      // Upgrade History (empty initially)
      upgradeHistory: [],

      // Metadata
      createdAt: now,
      createdBy: adminId,
      updatedAt: now,
      status: 'active',
      autoRenew: false, // Trial doesn't auto-renew
      
      // Trial specific fields
      isTrialActive: true,
      trialStartDate: now,
      trialEndDate: trialEndDate,
      
      // For legacy compatibility
      plan: 'pro',
      customLimits: proLimits
    };

    // Save to Firestore: companies/{companyId}/subscription_details/subscription
    const subscriptionRef = doc(appDb, `companies/${companyId}/subscription_details/subscription`);
    await setDoc(subscriptionRef, subscriptionData);

    console.log(`✅ PRO trial subscription created successfully for ${companyId}`);
    console.log(`📅 Trial ends at: ${trialEndDate.toLocaleDateString()}`);

    // Log the trial creation to subscription activities
    const activityRef = doc(appDb, `subscription_activities/${Date.now()}_${companyId}`);
    await setDoc(activityRef, {
      companyId,
      activityType: 'trial_created',
      description: '1-month PRO trial activated',
      createdBy: adminId,
      createdAt: now,
      trialEndDate: trialEndDate,
      plan: 'pro'
    });

    return {
      success: true,
      subscriptionData,
      trialEndDate,
      message: `1-month PRO trial activated until ${trialEndDate.toLocaleDateString()}`
    };

  } catch (error) {
    console.error(`❌ Error creating trial subscription for ${companyId}:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Bulk create trial subscriptions for multiple companies
 * @param {Object} appDb - Main app's Firestore database instance
 * @param {Array<string>} companyIds - Array of company IDs
 * @param {string} adminId - Admin ID
 * @returns {Promise<Object>} - Results of bulk creation
 */
export const bulkCreateTrialSubscriptions = async (appDb, companyIds, adminId) => {
  console.log(`🎁 Bulk creating trial subscriptions for ${companyIds.length} companies...`);
  
  const results = {
    total: companyIds.length,
    successful: 0,
    failed: 0,
    errors: []
  };

  for (const companyId of companyIds) {
    try {
      const result = await createTrialSubscriptionForCompany(appDb, companyId, adminId);
      
      if (result.success) {
        results.successful++;
      } else {
        results.failed++;
        results.errors.push({
          companyId,
          error: result.error
        });
      }
    } catch (error) {
      results.failed++;
      results.errors.push({
        companyId,
        error: error.message
      });
    }
  }

  console.log(`✅ Bulk trial creation complete: ${results.successful} successful, ${results.failed} failed`);
  
  return results;
};

/**
 * Check if a company's trial has expired
 * @param {Object} subscription - Subscription data
 * @returns {boolean} - True if trial has expired
 */
export const isTrialExpired = (subscription) => {
  if (!subscription || !subscription.isTrialActive || !subscription.trialEndDate) {
    return false;
  }

  const now = new Date();
  const trialEndDate = subscription.trialEndDate.toDate 
    ? subscription.trialEndDate.toDate() 
    : new Date(subscription.trialEndDate);

  return now > trialEndDate;
};

/**
 * Get trial remaining days
 * @param {Object} subscription - Subscription data
 * @returns {number} - Days remaining in trial
 */
export const getTrialRemainingDays = (subscription) => {
  if (!subscription || !subscription.isTrialActive || !subscription.trialEndDate) {
    return 0;
  }

  const now = new Date();
  const trialEndDate = subscription.trialEndDate.toDate 
    ? subscription.trialEndDate.toDate() 
    : new Date(subscription.trialEndDate);

  const diffTime = trialEndDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};



















































